'use client'

// ---------------------------------------------------------------------------
// Collections — shared control primitives (scale build).
//
// One interaction grammar across the library and the inspector: a segmented
// view/density switch and a faceted filter (a "Filter" accordion popover +
// removable chips). All calm and monochrome per the Martini system — no
// color, no dashed borders.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react'
import { useExtracted } from 'next-intl'
import { Check, ChevronDown, Filter, X } from '@/components/ui/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  COLLECTION_PROPERTY_TYPES_KEY,
  EXPERIMENTAL_FEATURES_CHANGED_EVENT,
  isCollectionPropertyTypesEnabled,
} from '@/lib/experimentalFeatures'
import { cn } from '@/lib/utils'
import { trackLibraryFilterApplied } from '@/lib/analytics'
import { useLibraryStore } from '@/store/libraryStore'
import type { LibraryPropertyType } from '@/types/shared'
import { PROPERTY_TYPES } from './propertyValues'

export type Filters = Record<string, string[]>

export function useCollectionPropertyTypesEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => isCollectionPropertyTypesEnabled())

  useEffect(() => {
    const sync = () => setEnabled(isCollectionPropertyTypesEnabled())
    const featureHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ feature?: string }>).detail
      if (detail?.feature === 'collectionPropertyTypes') sync()
    }
    const storageHandler = (event: StorageEvent) => {
      if (event.key === COLLECTION_PROPERTY_TYPES_KEY) sync()
    }
    window.addEventListener(EXPERIMENTAL_FEATURES_CHANGED_EVENT, featureHandler)
    window.addEventListener('storage', storageHandler)
    return () => {
      window.removeEventListener(EXPERIMENTAL_FEATURES_CHANGED_EVENT, featureHandler)
      window.removeEventListener('storage', storageHandler)
    }
  }, [])

  return enabled
}

// --- Icon select: a single-purpose toolbar control — icon + current value on
// the trigger (no catch-all "Display" nesting), one flat list to pick from. ---

export function IconSelect({
  icon,
  title,
  value,
  options,
  onChange,
  prefix,
  menuLabel,
}: {
  icon: React.ReactNode
  title: string
  value: string
  options: readonly string[]
  onChange: (v: string) => void
  /** Function label before the value ('Group:') so the control names itself. */
  prefix?: string
  /** Heading inside the menu ('Group by') so the list names its function. */
  menuLabel?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          title={title}
          className="flex h-7 items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          {icon}
          {prefix && <span className="text-muted-foreground/60">{prefix}</span>}
          {value}
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-44 p-1">
        {menuLabel && (
          <div className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
            {menuLabel}
          </div>
        )}
        {options.map(o => (
          <button
            key={o}
            onClick={() => {
              onChange(o)
              setOpen(false)
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-secondary"
          >
            <Check className={cn('size-3.5 shrink-0', o === value ? 'opacity-100' : 'opacity-0')} />
            <span className="flex-1 text-left">{o}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

// --- Property-type picker rows: the check-gutter row per type, shared by the
// property menu and the create-property popover. Close/skip logic (dismiss the
// popover, ignore same-type clicks) belongs to the caller's onSelect. ---------

export function PropertyTypeRows({
  selected,
  onSelect,
}: {
  selected: LibraryPropertyType
  onSelect: (type: LibraryPropertyType) => void
}) {
  return (
    <>
      {PROPERTY_TYPES.map(pt => (
        <button
          key={pt.type}
          onClick={() => onSelect(pt.type)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-secondary"
        >
          <Check className={cn('size-3.5 shrink-0', pt.type === selected ? 'opacity-100' : 'opacity-0')} />
          <span className="min-w-0 flex-1 truncate text-left">{pt.label}</span>
          <span className="shrink-0 text-[10px] text-muted-foreground/60">{pt.hint}</span>
        </button>
      ))}
    </>
  )
}

// --- Segmented tabs (view switch, density, scope) --------------------------

export type SegOption<T extends string> = { key: T; label: string; icon?: React.ReactNode; disabled?: boolean }

export function SegTabs<T extends string>({
  options,
  value,
  onChange,
  showLabels = true,
  className,
}: {
  options: SegOption<T>[]
  value: T
  onChange: (v: T) => void
  showLabels?: boolean
  className?: string
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn('flex items-center gap-0.5 rounded-md bg-secondary/40 p-0.5', className)}>
        {options.map(o => {
          const btn = (
            <button
              key={o.key}
              disabled={o.disabled}
              onClick={() => onChange(o.key)}
              className={cn(
                'flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors',
                value === o.key ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                o.disabled && 'cursor-not-allowed opacity-40',
              )}
            >
              {o.icon}
              {showLabels && <span className="capitalize">{o.label}</span>}
            </button>
          )
          // Icon-only tabs explain themselves on hover, fast (native title is
          // too slow to teach an unfamiliar control).
          if (showLabels) return btn
          return (
            <Tooltip key={o.key}>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent side="bottom" className="px-2 py-1 text-[11px]">
                {o.label}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}

// --- Faceted filter --------------------------------------------------------

export type FacetDef = { key: string; label: string; values: string[] }

function summarize(vals: string[]): string {
  if (vals.length === 1) return vals[0]
  return `${vals[0]} +${vals.length - 1}`
}

/** A "+ Filter" accordion popover plus removable chips. OR within a facet, AND
 * across facets. `counts(facet)` returns value→count given the other filters. */
export function FilterControl({
  facets,
  filters,
  counts,
  onChange,
  compact,
  labelFor,
}: {
  facets: FacetDef[]
  filters: Filters
  counts: (facet: string) => Record<string, number>
  onChange: (f: Filters) => void
  compact?: boolean
  /** Resolve a facet token (option id / raw kind) to its display label. Values are
   * stored as rename-proof tokens; default is identity for plain-string facets. */
  labelFor?: (facetKey: string, value: string) => string
}) {
  const t = useExtracted()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const activeKeys = Object.keys(filters).filter(k => filters[k]?.length)
  // A filter that can't narrow anything is noise — hide until values exist
  // (but never while a filter is active, so it can always be cleared).
  const hasValues = facets.some(f => Object.keys(counts(f.key)).length > 0)
  if (!hasValues && activeKeys.length === 0) return null
  const label = labelFor ?? ((_k: string, v: string) => v)

  const toggleValue = (facet: string, value: string) => {
    const cur = filters[facet] ?? []
    const adding = !cur.includes(value)
    const next = adding ? [...cur, value] : cur.filter(v => v !== value)
    const merged = { ...filters, [facet]: next }
    if (!next.length) delete merged[facet]
    onChange(merged)
    if (adding) trackLibraryFilterApplied({ workspaceId: useLibraryStore.getState().workspaceId, facet })
  }
  const clearFacet = (facet: string) => {
    const merged = { ...filters }
    delete merged[facet]
    onChange(merged)
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
              activeKeys.length && 'text-foreground',
            )}
            title={t('Filter')}
          >
            <Filter className="size-3.5" />
            {!compact && t('Filter')}
            <ChevronDown className="size-3" />
            {activeKeys.length > 0 && <span className="tabular-nums">{activeKeys.length}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1">
          <div className="max-h-80 overflow-auto">
            {facets.map(f => {
              const isOpen = expanded === f.key
              const vc = isOpen ? counts(f.key) : {}
              const sel = filters[f.key] ?? []
              return (
                <div key={f.key}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : f.key)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs text-foreground hover:bg-secondary"
                  >
                    <span>{f.label}</span>
                    {sel.length > 0 && <span className="text-[10px] text-muted-foreground">{sel.length}</span>}
                  </button>
                  {isOpen && (
                    <div className="pb-1">
                      {f.values
                        .map(v => ({ v, n: vc[v] ?? 0 }))
                        .sort((a, b) => b.n - a.n)
                        .map(({ v, n }) => (
                          <button
                            key={v}
                            onClick={() => toggleValue(f.key, v)}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-secondary',
                              n === 0 ? 'text-muted-foreground/40' : 'text-foreground',
                            )}
                          >
                            <Check className={cn('size-3.5', sel.includes(v) ? 'opacity-100' : 'opacity-0')} />
                            <span className="flex-1 text-left">{label(f.key, v)}</span>
                            <span className="text-[10px] tabular-nums text-muted-foreground/60">{n}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>

      {activeKeys.map(k => {
        const def = facets.find(f => f.key === k)
        return (
          <span
            key={k}
            className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2 py-0.5 text-xs text-foreground"
          >
            <span className="text-muted-foreground">{def?.label ?? k}:</span>
            {summarize(filters[k].map(v => label(k, v)))}
            <button
              onClick={() => clearFacet(k)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={t('Remove filter')}
            >
              <X className="size-3" />
            </button>
          </span>
        )
      })}
      {activeKeys.length > 1 && (
        <button onClick={() => onChange({})} className="text-[11px] text-muted-foreground hover:text-foreground">
          {t('Clear')}
        </button>
      )}
    </div>
  )
}
